import type { BodyPosition, SkyCatalog, SkyObjectPosition, StarPosition } from "./types";

export interface SearchResult {
  id: string;
  label: string;
  subtitle: string;
  kind: "star" | "body" | "constellation";
  hr?: number;
}

export interface BestTargetOptions {
  minAltitudeDeg?: number;
  maxMagnitude?: number;
  limit?: number;
}

export function buildSearchResults(
  query: string,
  catalog: SkyCatalog,
  stars: StarPosition[],
  bodies: BodyPosition[],
): SearchResult[] {
  const term = query.trim().toLowerCase();
  if (!term) return [];

  const starResults = stars
    .filter((position) =>
      [
        position.star.properName,
        position.star.designation,
        `hr ${position.hr}`,
        `hr${position.hr}`,
        position.star.spectralType,
      ].some((text) => text.toLowerCase().includes(term)),
    )
    .map((position) => ({
      id: position.id,
      label: position.label,
      subtitle: position.subtitle,
      kind: "star" as const,
      hr: position.hr,
    }));

  const bodyResults = bodies
    .filter((body) => body.label.toLowerCase().includes(term))
    .map((body) => ({
      id: body.id,
      label: body.label,
      subtitle: body.subtitle,
      kind: "body" as const,
    }));

  const constellationResults = catalog.constellations
    .filter((constellation) =>
      [constellation.name, constellation.iau, constellation.shapeHint]
        .some((text) => text.toLowerCase().includes(term)),
    )
    .map((constellation) => ({
      id: `const:${constellation.iau}`,
      label: constellation.name,
      subtitle: `Constellation | ${constellation.shapeHint}`,
      kind: "constellation" as const,
    }));

  return [...starResults, ...bodyResults, ...constellationResults]
    .sort((a, b) => score(a, term) - score(b, term) || a.label.localeCompare(b.label))
    .slice(0, 10);
}

export function visibleNowObjects(objects: SkyObjectPosition[]): SkyObjectPosition[] {
  return objects
    .filter((object) => object.altitudeDeg >= 5)
    .sort((a, b) => a.magnitude - b.magnitude || b.altitudeDeg - a.altitudeDeg)
    .slice(0, 12);
}

export function bestTargetStars(stars: StarPosition[], options: BestTargetOptions = {}): StarPosition[] {
  const minAltitudeDeg = options.minAltitudeDeg ?? 12;
  const maxMagnitude = options.maxMagnitude ?? 3.5;
  const limit = options.limit ?? 12;

  return stars
    .filter((star) => star.altitudeDeg >= minAltitudeDeg && star.magnitude <= maxMagnitude)
    .sort((a, b) => targetScore(b) - targetScore(a) || a.magnitude - b.magnitude)
    .slice(0, limit);
}

function score(result: SearchResult, term: string): number {
  const label = result.label.toLowerCase();
  if (label === term) return 0;
  if (label.startsWith(term)) return 1;
  if (result.kind === "body") return 2;
  if (result.kind === "star") return 3;
  return 4;
}

function targetScore(star: StarPosition): number {
  const brightness = Math.max(0, 6 - star.magnitude) * 12;
  const altitude = Math.min(80, star.altitudeDeg) * 0.55;
  const sweetSpot = star.altitudeDeg >= 25 && star.altitudeDeg <= 75 ? 8 : 0;
  return brightness + altitude + sweetSpot;
}
